import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { ReplaySubject, timer } from 'rxjs';

import { BrokerMessageModel } from '@data-layer/models/entry-summaries';
import { enumBrokerMessageDirection } from '@data-layer/enums/entry-summaries';
import { CUSTOM_MASK } from '@helpers/custom-validator';

@Component({
  selector: 'app-broker-messages-chat',
  templateUrl: './broker-messages-chat.component.html',
  styleUrls: ['./broker-messages-chat.component.scss'],
})
export class BrokerMessagesChatComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() public companyLogoUrl: string;
  @Input() public messages: BrokerMessageModel[] = [];
  @Input() public companyName: string = 'BROKER_CHAT_DIRECTION.YOU';
  @Input() public brokerName: string = 'BROKER_CHAT_DIRECTION.YOUR_BROKER';
  @Input() public isCanSent: boolean = true;
  @Output() public sent: EventEmitter<string> = new EventEmitter<string>();

  public enumBrokerMessageDirection: typeof enumBrokerMessageDirection = enumBrokerMessageDirection;
  public formControl: UntypedFormControl = new UntypedFormControl(null);
  public CUSTOM_MASK = CUSTOM_MASK;
  public textareaHeight: number = 44;

  @ViewChild('scrollingContainer', { static: false })
  public scrollingContainer: ElementRef;
  @ViewChild('textareaDuplicate', { static: false })
  public textareaDuplicate: ElementRef;

  private isDestroy: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  constructor() {}

  public ngOnInit(): void {
    this.formControl.valueChanges.pipe(takeUntil(this.isDestroy)).subscribe(() => {
      this.setTextareaHeight();
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.messages) {
      this.scrollToBottom();
    }
  }

  public ngAfterViewInit(): void {
    this.scrollToBottom();
  }

  public ngOnDestroy(): void {
    this.isDestroy.next(true);
    this.isDestroy.complete();
  }

  public sendInputPressKey(e: KeyboardEvent): void {
    const enterKey: string = 'Enter';
    if (e.code === enterKey && e.shiftKey) {
      return;
    }

    if (e.code === enterKey && this.formControl.value) {
      e.preventDefault();
      this.onSent();
    }
  }

  public onSent(): void {
    this.sent.emit(this.formControl.value);
    this.formControl.reset();
  }

  public scrollToBottom() {
    timer(100)
      .pipe(takeUntil(this.isDestroy))
      .subscribe(() => {
        if (!this.scrollingContainer || !this.scrollingContainer.nativeElement) {
          return;
        }
        const scrollHeight: number = this.scrollingContainer.nativeElement.scrollHeight || 0;
        const offsetHeight: number = this.scrollingContainer.nativeElement.offsetHeight || 0;
        if (scrollHeight > offsetHeight) {
          this.scrollingContainer.nativeElement.scrollTo({
            behavior: 'smooth',
            top: scrollHeight,
          });
        }
      });
  }

  public trackByFn(i: BrokerMessageModel): number {
    return i.id;
  }

  public getSenderLabelKey(message: BrokerMessageModel): string {
    if (message.direction !== enumBrokerMessageDirection.TO_BROKER) {
      return this.brokerName;
    }
    return message.isFromImporterOfRecord ? 'BROKER_CHAT_DIRECTION.IMPORTER_OF_RECORD' : this.companyName;
  }

  private setTextareaHeight() {
    timer(0)
      .pipe(takeUntil(this.isDestroy))
      .subscribe(() => {
        this.textareaHeight = this.textareaDuplicate.nativeElement.scrollHeight + 2;
      });
  }
}
